import React, { useEffect, useState, useMemo } from "react";
import {
  VStack,
  HStack,
  Button,
  Box,
  Text,
  Badge,
  Spinner,
  Wrap,
  WrapItem,
  FormControl,
  FormLabel,
  Input,
  Select,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Icon,
} from "@chakra-ui/react";
import DarwinAgentsSelection from "modules/eva/components/DarwinAgentsSelection";
import { AppIcons } from "constants/icons";
import PromptSelectionTable from "modules/eva/components/PromptSelectionTable";
import ChatPage from "pages/chat/ChatPage";
import { generateSystemPrompt, fetchStepData, runExperiment } from "services/EvaServices";
import showToast from "hooks/useCustomToast";
import LibraryAnalysts from "modules/eva/components/LibraryAnalysts";
import AnalystScenariosSelection from "modules/eva/components/AnalystScenariosSelection";
import {
  AgentInputProto,
  AgentInputProto_OverrideVariables,
  ChatClientType,
  chatClientTypeToJSON,
  ChatContext,
  ChatRequest,
} from "build-ts/chat_session";
import { ChatMessageType, DarwinAgentType, DarwinHeader, darwinAgentTypeFromJSON } from "build-ts/common";
import { generateRandomSuffix } from "utils/nameUtils";

interface Step {
  id: string;
  title: string;
  description: string;
  component: React.ComponentType<{
    onSelectionChange?: (selectedItems: Set<string>, agentType?: string) => void;
    selectedItems?: Set<string>;
    hideActions?: boolean;
    singleSelection?: boolean;
    selectedAgentId?: string;
    selectedAnalystId?: string;
    onValidationChange?: (isValid: boolean) => void;
    onClearSelection?: () => void;
  }>;
}

interface EvaPlaygroundProps {
  initialDarwinAgentId?: string;
  initialDarwinAgentType?: DarwinAgentType;
  initialPromptId?: string;
  startAtStepIndex?: number;
  userId?: string;
  /** Called after a chat is saved/renamed/deleted so EvaList can refresh its session list. */
  onEvaSessionsRefresh?: () => void;
}

const EvaPlayground: React.FC<EvaPlaygroundProps> = ({
  initialDarwinAgentId,
  initialDarwinAgentType,
  initialPromptId,
  startAtStepIndex,
  userId = "",
  onEvaSessionsRefresh,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedItems, setSelectedItems] = useState<Record<string, Set<string>>>({});
  const [generatedPrompt, setGeneratedPrompt] = useState("");
  const [sessionHeader, setSessionHeader] = useState<DarwinHeader | undefined>(undefined);
  const [isGenerating, setIsGenerating] = useState(false);
  const [dateOverride, setDateOverride] = useState<string>("");
  const [companyName, setCompanyName] = useState<string>("");
  const [weekStartDay, setWeekStartDay] = useState<string>("");
  const [quarterStartMonth, setQuarterStartMonth] = useState<string>("");
  const [selectedAgentType, setSelectedAgentType] = useState<DarwinAgentType | undefined>(initialDarwinAgentType);
  const {
    isOpen: isExperimentModalOpen,
    onOpen: onOpenExperimentModal,
    onClose: onCloseExperimentModal,
  } = useDisclosure();
  const [experimentFile, setExperimentFile] = useState<File | null>(null);
  const [isRunningExperiment, setIsRunningExperiment] = useState(false);
  const [experimentName, setExperimentName] = useState<string>("");

  const selectedAnalysts = selectedItems["analysts"] || new Set<string>();
  const hasAnalysts = selectedAnalysts.size > 0;

  const steps: Step[] = useMemo(() => {
    const baseSteps: Step[] = [
      {
        id: "darwin_agents",
        title: "Select Agent",
        description: "Select Darwin agents for your analysis",
        component: DarwinAgentsSelection,
      },
      {
        id: "prompts",
        title: "Select Prompt",
        description: "Select prompts for the chosen Darwin agent",
        component: PromptSelectionTable,
      },
      {
        id: "analysts",
        title: "Select Analyst",
        description: "Select analysts for your analysis",
        component: LibraryAnalysts,
      },
    ];

    let allSteps = [...baseSteps];

    const analystsStepIndex = allSteps.findIndex(step => step.id === "analysts");

    if (hasAnalysts && selectedAgentType !== DarwinAgentType.DATA_SCOPE_AGENT) {
      allSteps.splice(analystsStepIndex + 1, 0, {
        id: "jobs",
        title: "Select Jobs",
        description: "Select scheduled jobs from the chosen analyst",
        component: AnalystScenariosSelection,
      });
    }

    return allSteps;
  }, [hasAnalysts, selectedAgentType]);

  const handleItemSelection = (stepId: string, selectedItems: Set<string>, agentType?: string) => {
    setSelectedItems(prev => ({
      ...prev,
      [stepId]: selectedItems,
    }));

    if (stepId === "darwin_agents") {
      if (agentType) {
        const type = darwinAgentTypeFromJSON(agentType);
        setSelectedAgentType(type);
      } else {
        setSelectedAgentType(undefined);
      }
    }
  };

  const handleClearSelection = (stepId: string) => {
    setSelectedItems(prev => ({
      ...prev,
      [stepId]: new Set(),
    }));
  };

  useEffect(() => {
    const preselected: Record<string, Set<string>> = {};
    if (initialDarwinAgentId) {
      preselected["darwin_agents"] = new Set([initialDarwinAgentId]);
    }
    if (initialPromptId) {
      preselected["prompts"] = new Set([initialPromptId]);
    }
    if (Object.keys(preselected).length > 0) {
      setSelectedItems(prev => ({ ...prev, ...preselected }));
    }
    if (typeof startAtStepIndex === "number" && startAtStepIndex >= 0) {
      setCurrentStep(startAtStepIndex);
    } else if (initialDarwinAgentId && initialPromptId) {
      setCurrentStep(2);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDarwinAgentId, initialPromptId, startAtStepIndex]);

  const handleNext = () => {
    if (currentStep === 0) {
      const selectedAgents = selectedItems["darwin_agents"] || new Set();
      if (selectedAgents.size !== 1) {
        showToast("Selection Required", "Please select exactly 1 Darwin agent to proceed.", "warning");
        return;
      }
    } else if (currentStep === 1) {
      const selectedPrompts = selectedItems["prompts"] || new Set();
      if (selectedPrompts.size !== 1) {
        showToast("Selection Required", "Please select exactly 1 prompt to proceed.", "warning");
        return;
      }
    }

    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const buildChatRequestFromSelection = async (): Promise<ChatRequest | null> => {
    const selectedDarwinAgentId = Array.from(selectedItems["darwin_agents"] || new Set())[0];
    const selectedPromptId = Array.from(selectedItems["prompts"] || new Set())[0];

    if (!selectedDarwinAgentId || !selectedPromptId) {
      showToast("Selection Required", "Please select exactly 1 Darwin agent and 1 prompt.", "warning");
      return null;
    }

    const stepDataPromises = Object.entries(selectedItems).map(async ([stepId, ids]) => {
      const idArray = Array.from(ids);
      if (idArray.length === 0) return { stepId, data: [] };
      const data = await fetchStepData(stepId, idArray);
      return { stepId, data };
    });

    const stepDataResults = await Promise.all(stepDataPromises);
    const stepDataMap = stepDataResults.reduce(
      (acc, { stepId, data }) => {
        acc[stepId] = data;
        return acc;
      },
      {} as Record<string, any[]>
    );

    const analysts = stepDataMap["analysts"] || [];
    const jobs = stepDataMap["jobs"] || [];

    const overrideVars: any = {};
    if (dateOverride) {
      overrideVars.today_date = dateOverride;
    }
    if (companyName) {
      overrideVars.company_name = companyName;
    }
    if (weekStartDay) {
      overrideVars.week_start_day = weekStartDay;
    }
    if (quarterStartMonth) {
      overrideVars.quarter_start_month = quarterStartMonth;
    }

    const agentInputData: any = {
      analyst: analysts.filter(analyst => analyst !== null)[0],
      scenario_jobs: jobs.filter((job: any) => job !== null),
    };

    if (Object.keys(overrideVars).length > 0) {
      agentInputData.override_jinja_variables = AgentInputProto_OverrideVariables.create(overrideVars);
    }

    const chatContextData: any = {
      agent_header: DarwinHeader.create({
        id: selectedDarwinAgentId,
      }),
      prompt_header: DarwinHeader.create({
        id: selectedPromptId,
      }),
      agent_input: AgentInputProto.create(agentInputData),
    };

    const chatContext: ChatContext = ChatContext.create(chatContextData);

    const chatRequest: ChatRequest = ChatRequest.create({
      session_header: DarwinHeader.create({
        id: "",
        name: `New Chat ${generateRandomSuffix(8)}`,
        type: chatClientTypeToJSON(ChatClientType.EVA),
        author_id: userId,
      }),
      chat_context: chatContext,
    });

    return chatRequest;
  };

  const handleGenerateTemplate = async () => {
    setIsGenerating(true);

    try {
      const chatRequest = await buildChatRequestFromSelection();
      if (!chatRequest) {
        return;
      }

      const response = await generateSystemPrompt(chatRequest);

      if (response && response.session_header?.id) {
        setSessionHeader(response.session_header);
        let systemMessage = "";
        response?.messages?.forEach(message => {
          if (message.message_type === ChatMessageType.SYSTEM) {
            systemMessage = message.content || "";
          }
        });
        setGeneratedPrompt(systemMessage);
        showToast("Template Generated", "Your agent has been initialized successfully!", "success");
      } else {
        throw new Error("No response received from the API");
      }
    } catch (error) {
      console.error("Error generating template:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRunExperiment = async () => {
    onOpenExperimentModal();
  };

  const handleConfirmExperiment = async () => {
    if (!experimentFile) {
      showToast("CSV Required", "Please upload a CSV file to run the experiment.", "warning");
      return;
    }
    if (!experimentName.trim()) {
      showToast("Experiment Name Required", "Please enter an experiment name.", "warning");
      return;
    }

    setIsRunningExperiment(true);
    try {
      const chatRequest = await buildChatRequestFromSelection();
      if (!chatRequest) {
        return;
      }
      const result = await runExperiment(chatRequest, experimentName, experimentFile);
      showToast(`Experiment ${experimentName} started with ${result.total_rows} questions`, "", "success");
      onCloseExperimentModal();
    } catch (error) {
      console.error("Error running experiment:", error);
    } finally {
      setExperimentFile(null);
      setExperimentName("");
      setIsRunningExperiment(false);
    }
  };

  const isCurrentStepValid = () => {
    if (currentStep === 0) {
      const selectedAgents = selectedItems["darwin_agents"] || new Set();
      return selectedAgents.size === 1;
    } else if (currentStep === 1) {
      const selectedPrompts = selectedItems["prompts"] || new Set();
      return selectedPrompts.size === 1;
    }
    return true;
  };

  if (generatedPrompt) {
    const selectedAnalystId = selectedAnalysts.size > 0 ? Array.from(selectedAnalysts)[0] : undefined;

    return (
      <ChatPage
        systemPrompt={generatedPrompt}
        isFromEva={true}
        initialChatSessionHeader={sessionHeader}
        initialAnalystId={selectedAnalystId}
        onEvaSessionsRefresh={onEvaSessionsRefresh}
      />
    );
  }

  return (
    <VStack align="stretch" spacing={3} height="100%" overflow="hidden">
      <HStack justify="space-between" w="full" px={3} pt={4} flexShrink={0}>
        <HStack spacing={3}>
          <Icon as={AppIcons.playground} boxSize={9} color="fg.default" />
          <Text fontSize="lg" mb={0} fontWeight="700">
            Playground
          </Text>
        </HStack>

        <Wrap spacing={2} align="center">
          {steps.map((step, index) => (
            <React.Fragment key={step.id}>
              <WrapItem>
                <Badge
                  color={index <= currentStep ? "white" : "gray"}
                  variant={index === currentStep ? "solid" : "outline"}
                  bg={index <= currentStep ? "purple.700" : "gray.200"}
                  px={3}
                  py={1}
                  borderRadius="full"
                  size="xl"
                  fontSize="sm"
                >
                  <HStack>
                    {index < currentStep ? <Icon as={AppIcons.checkFilled} boxSize={4} color="white" /> : null}
                    <Text mb={0}>
                      {index + 1}. {step.title}
                    </Text>
                  </HStack>
                </Badge>
              </WrapItem>
              {index < steps.length - 1 && (
                <WrapItem>
                  <Text mb={0}>→</Text>
                </WrapItem>
              )}
            </React.Fragment>
          ))}
        </Wrap>
      </HStack>

      <Box
        mx={4}
        px={4}
        py={2}
        flexShrink={0}
        border="2px solid"
        borderColor="gray.300"
        borderRadius="md"
        shadow="sm"
        bg="gray.50"
      >
        <HStack>
          <FormControl>
            <FormLabel fontSize="sm" mb={2} fontWeight="600">
              Override Date
            </FormLabel>
            <Input
              size="sm"
              type="date"
              value={dateOverride || ""}
              onChange={e => setDateOverride(e.target.value || "")}
              bg="white"
              borderColor="gray.300"
              _hover={{ borderColor: "blue.400" }}
              fontWeight="500"
              color="black"
            />
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm" mb={2} fontWeight="600">
              Company Name
            </FormLabel>
            <Input
              size="sm"
              type="text"
              value={companyName || ""}
              onChange={e => setCompanyName(e.target.value || "")}
              placeholder="Enter company name"
              bg="white"
              borderColor="gray.300"
              _hover={{ borderColor: "blue.400" }}
              fontWeight="500"
              color="black"
            />
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm" mb={2} fontWeight="600">
              Week Start Day
            </FormLabel>
            <Select
              size="sm"
              value={weekStartDay}
              onChange={e => setWeekStartDay(e.target.value)}
              bg="white"
              borderColor="gray.300"
              _hover={{ borderColor: "blue.400" }}
              fontWeight="500"
              color="black"
            >
              <option value="">Select week start day</option>
              <option value="Monday">Monday</option>
              <option value="Tuesday">Tuesday</option>
              <option value="Wednesday">Wednesday</option>
              <option value="Thursday">Thursday</option>
              <option value="Friday">Friday</option>
              <option value="Saturday">Saturday</option>
              <option value="Sunday">Sunday</option>
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm" mb={2} fontWeight="600">
              Quarter Start Month
            </FormLabel>
            <Select
              size="sm"
              value={quarterStartMonth}
              onChange={e => setQuarterStartMonth(e.target.value)}
              bg="white"
              borderColor="gray.300"
              _hover={{ borderColor: "blue.400" }}
              fontWeight="500"
              color="black"
            >
              <option value="">Select quarter start month</option>
              <option value="January">January</option>
              <option value="February">February</option>
              <option value="March">March</option>
              <option value="April">April</option>
              <option value="May">May</option>
              <option value="June">June</option>
              <option value="July">July</option>
              <option value="August">August</option>
              <option value="September">September</option>
              <option value="October">October</option>
              <option value="November">November</option>
              <option value="December">December</option>
            </Select>
          </FormControl>
        </HStack>
      </Box>

      <Box flex="1" minHeight={0} overflow="auto" width="100%" ml={4}>
        {React.createElement(steps[currentStep].component, {
          onSelectionChange: (selectedItems: Set<string>, agentType?: string) =>
            handleItemSelection(steps[currentStep].id, selectedItems, agentType),
          selectedItems: selectedItems[steps[currentStep].id] || new Set(),
          hideActions: true,
          singleSelection: steps[currentStep].id === "custom_agents" || steps[currentStep].id === "analysts",
          selectedAgentId:
            steps[currentStep].id === "prompts"
              ? Array.from(selectedItems["darwin_agents"] || new Set())[0]
              : undefined,
          selectedAnalystId:
            steps[currentStep].id === "jobs" ? Array.from(selectedItems["analysts"] || new Set())[0] : undefined,
          onClearSelection:
            steps[currentStep].id === "custom_agents" || steps[currentStep].id === "analysts"
              ? () => handleClearSelection(steps[currentStep].id)
              : undefined,
        })}
      </Box>

      <HStack justify="space-between" ml={6} mr={6} pb={2} flexShrink={0}>
        <Button
          leftIcon={<Icon as={AppIcons.leftIcon} />}
          onClick={handlePrevious}
          isDisabled={currentStep === 0}
          variant="outline"
        >
          Previous
        </Button>
        {currentStep === steps.length - 1 ? (
          <Box>
            <Button
              colorScheme="blue"
              onClick={handleRunExperiment}
              isLoading={isRunningExperiment}
              loadingText="Starting..."
              rightIcon={isRunningExperiment ? <Spinner size="sm" /> : undefined}
              mr={2}
              leftIcon={<Icon as={AppIcons.flask} />}
              bg="green.600"
              color="white"
              _hover={{ bg: "green.700" }}
              size="lg"
            >
              Run Experiment
            </Button>
            <Button
              colorScheme="purple"
              bg="purple.700"
              color="white"
              _hover={{ bg: "purple.600" }}
              onClick={handleGenerateTemplate}
              isLoading={isGenerating}
              loadingText="Generating..."
              rightIcon={isGenerating ? <Spinner size="sm" /> : undefined}
              leftIcon={<Icon as={AppIcons.generate} />}
              size="lg"
            >
              Generate Prompt
            </Button>
          </Box>
        ) : (
          <Button
            rightIcon={<Icon as={AppIcons.rightIcon} />}
            onClick={handleNext}
            colorScheme={isCurrentStepValid() ? "blue" : "gray"}
            isDisabled={!isCurrentStepValid()}
          >
            Next
          </Button>
        )}
      </HStack>

      <Modal
        isOpen={isExperimentModalOpen}
        onClose={isRunningExperiment ? () => {} : onCloseExperimentModal}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Run Experiment</ModalHeader>
          <ModalCloseButton disabled={isRunningExperiment} />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <Text fontSize="sm" color="gray.600">
                Upload a CSV file with questions to run experiment
              </Text>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="600">
                  Experiment Name
                </FormLabel>
                <Input
                  type="text"
                  value={experimentName}
                  onChange={e => setExperimentName(e.target.value)}
                  placeholder="Enter experiment name"
                  bg="white"
                  borderColor="gray.300"
                  size="sm"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="600">
                  CSV File
                </FormLabel>
                <Input
                  type="file"
                  accept=".csv"
                  onChange={e => {
                    const file = e.target.files?.[0] || null;
                    setExperimentFile(file);
                  }}
                  bg="white"
                  borderColor="gray.300"
                  size="sm"
                />
                {experimentFile && (
                  <Text fontSize="xs" color="gray.600" mt={1}>
                    Selected file: {experimentFile.name}
                  </Text>
                )}
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onCloseExperimentModal} isDisabled={isRunningExperiment}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleConfirmExperiment}
              isLoading={isRunningExperiment}
              loadingText="Starting..."
              isDisabled={!experimentName.trim() || !experimentFile || isRunningExperiment}
            >
              Start Experiment
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default EvaPlayground;
